import { AccountingIntegrationSettings } from '@accounting-integrations/interfaces/accounting-integrations';
import { AdvancedPrefixManagement } from '@accounting-integrations/interfaces/advanced-prefix-management';
import { AccessControlConfigInterface } from './access-control-config-interface';

export interface MediaImgField {
  url: string;
}
export interface GlobalSetting {
  id: number;
  name: string;
  vat_number: string;
  street: string;
  street2: string;
  country: string;
  zip: string;
  city: string;
  phone1: string;
  website: string;
  sending_center_name: string;
  email1: string;
  default_language: string;
  available_languages: string[];
  home_page: string;
  logo_url: MediaImgField;
  background: MediaImgField;
  header_image_url: MediaImgField;
}
export interface UsersSetting {
  id: number;
  allow_access_client_section: string;
  header_login_text: string;
  contact_profile_home_page: string;
  show_menu_entries: string[];
  show_menu_entries_for_organization: string[];
  allow_client_registration: string;
  header_register_text: string;
  show_birthdate: string;
  show_gender: string;
  show_address_fields: string;
  show_phone: string;
  show_phone2: string;
  show_email2: string;
  show_id_card_number: string;
  show_passport_number: string;
  show_registration_number: string;
  show_external_reference: string;
  show_nationality: string;
  show_health_information: string;
}
export interface ContactsSetting {
  id: number;
  contact_code_prefix: string;
  contact_code_number_length: string;
  contact_code_last_number: string;
  default_value_for_free_contact_bookings: string;
  mandatory_birthdate: string;
  mandatory_gender: string;
  mandatory_address_fields: string;
  mandatory_email: string;
  mandatory_language: string;
  mandatory_phone: string;
  mandatory_id_card_number: string;
  postal_codes_for_default_price_category: string;
  default_price_category_id_for_postal_codes: string;
  manage_custom_contact_fields: string;
  custom_contact_field_label_1: string;
  custom_contact_field_type_1: string;
  custom_contact_field_options_1: string;
  empty_section_for_space_2: string;
  custom_contact_field_label_2: string;
  custom_contact_field_type_2: string;
  custom_contact_field_options_2: string;
  empty_section_for_space_3: string;
  custom_contact_field_label_3: string;
  custom_contact_field_type_3: string;
  custom_contact_field_options_3: string;
  empty_section_for_space_4: string;
  custom_contact_field_label_4: string;
  custom_contact_field_type_4: string;
  custom_contact_field_options_4: string;
  empty_section_for_space_5: string;
  custom_contact_field_label_5: string;
  custom_contact_field_type_5: string;
  custom_contact_field_options_5: string;
  empty_section_for_space_6: string;
  custom_contact_field_label_6: string;
  custom_contact_field_type_6: string;
  custom_contact_field_options_6: string;
  empty_section_for_space_7: string;
  custom_contact_field_label_7: string;
  custom_contact_field_type_7: string;
  custom_contact_field_options_7: string;
  empty_section_for_space_8: string;
  custom_contact_field_label_8: string;
  custom_contact_field_type_8: string;
  custom_contact_field_options_8: string;
  empty_section_for_space_9: string;
  custom_contact_field_label_9: string;
  custom_contact_field_type_9: string;
  custom_contact_field_options_9: string;
  empty_section_for_space_10: string;
  custom_contact_field_label_10: string;
  custom_contact_field_type_10: string;
  custom_contact_field_options_10: string;
  send_messages_to_email_2: string;
}
export interface SubscriptionsSetting {
  id: number;
  send_subscription_confirmation_email: boolean;
  subscription_confirmation_email_title: string;
  subscription_confirmation_email_template_id: number;
  email_blind_copy_subscription_confirmation: string;
  admin_send_subscription_confirmation_email: boolean;
  send_subscription_cancellation_email: boolean;
  subscription_cancellation_email_title: string;
  subscription_cancellation_email_template_id: number;
  email_blind_copy_subscription_cancellation: string;
  admin_send_subscription_cancellation_email: boolean;
  send_subscription_recredit_email: boolean;
  subscription_recredit_email_title: string;
  subscription_recredit_email_template_id: number;
  email_blind_copy_subscription_recredit: string;
  admin_send_subscription_recredit_email: boolean;
  subscription_payment_request_email_title: string;
  subscription_payment_request_email_template_id: number;
  email_blind_copy_subscription_payment_request: string;
  subscription_payment_reminder_email_title: string;
  subscription_payment_reminder_email_template_id: number;
  email_blind_copy_subscription_payment_reminder: string;
}
export interface BookingsSetting {
  id: number;
  default_slot_duration: number;
  default_start_hour: string;
  default_end_hour: string;
  source_color_to_display_booking_calendar: string;
  allow_booking_refunds: boolean;
  cancelled_paid_booking_generate_refund: boolean;
  cancelled_unpaid_booking_remain_due: boolean;
  send_booking_confirmation_email: boolean;
  booking_confirmation_email_title: string;
  booking_confirmation_email_template_id: string;
  email_blind_copy_booking_confirmation: string;
  admin_send_booking_confirmation_email: boolean;
  send_booking_requested_email: boolean;
  booking_requested_email_title: string;
  booking_requested_email_template_id: string;
  email_blind_copy_booking_requested: string;
  send_booking_refused_email: boolean;
  booking_refused_email_title: string;
  booking_refused_email_template_id: string;
  email_blind_copy_booking_refused: string;
  send_booking_cancellation_email: boolean;
  booking_cancellation_email_title: string;
  booking_cancellation_email_template_id: string;
  booking_event_cancellation_email_template_id: string;
  email_blind_copy_booking_cancellation: string;
  admin_send_booking_cancellation_email: boolean;
  send_booking_event_reminder_email: boolean;
  booking_event_reminder_email_time: string;
  booking_event_reminder_email_title: string;
  booking_event_reminder_email_template_id: string;
  send_booking_event_change_email: boolean;
  booking_event_change_email_title: string;
  booking_event_change_email_template_id: string;
  admin_send_booking_event_change_email: boolean;
  send_booking_incident_email: boolean;
  booking_incident_email_profile_id: string;
  email_blind_copy_booking_incident: string;
}
export interface ActivitiesSetting {
  id: number;
  show_activity_categories_in_profile: string;
  show_activity_management_page: string;
  send_enrollment_confirmation_email: boolean;
  enrollment_confirmation_email_title: string;
  enrollment_confirmation_email_template_id: number;
  email_blind_copy_enrollment_confirmation: string;
  admin_send_enrollment_confirmation_email: boolean;
  send_enrollment_cancellation_email: boolean;
  enrollment_cancellation_email_title: string;
  enrollment_cancellation_email_template_id: number;
  email_blind_copy_enrollment_cancellation: string;
  admin_send_enrollment_cancellation_email: boolean;
  send_waiting_list_email: boolean;
  waiting_list_email_title: string;
  waiting_list_email_template_id: number;
  email_blind_copy_enrollment_waiting_list: string;
  send_again_available_email: boolean;
  again_available_email_title: string;
  again_available_email_template_id: number;
  email_blind_copy_enrollment_again_available: string;
  send_waiting_list_session_email: boolean;
  waiting_list_session_email_title: string;
  waiting_list_session_email_template_id: number;
  waiting_list_session_email_blind_copy: string;
  send_again_available_session_email: boolean;
  again_available_session_email_title: string;
  again_available_session_email_template_id: number;
  again_available_session_email_blind_copy: string;
  send_attendee_created_email: boolean;
  attendee_created_email_title: string;
  attendee_created_email_template_id: number;
  email_blind_copy_attendee_created: string;
  admin_send_attendee_created_email: boolean;
  send_attendee_cancelled_email: boolean;
  attendee_cancelled_email_title: string;
  attendee_cancelled_email_template_id: number;
  email_blind_copy_attendee_cancelled: string;
  admin_send_attendee_cancelled_email: boolean;
  send_session_updated_email: boolean;
  session_updated_email_title: string;
  session_updated_email_template_id: number;
  email_blind_copy_session_updated: string;
  admin_send_session_updated_email: boolean;
  enrollment_payment_request_email_title: string;
  enrollment_payment_request_email_template_id: number;
  email_blind_copy_enrollment_payment_request: string;
  enrollment_payment_reminder_email_title: string;
  enrollment_payment_reminder_email_template_id: number;
  email_blind_copy_enrollment_payment_reminder: string;
}
export interface AccountingSetting {
  id: number;
  strict_accounting: boolean;
  activate_sepa_direct_debit: boolean;
  sepa_name_of_the_legal_entity: string;
  sepa_street: string;
  sepa_street2: string;
  sepa_zip: string;
  sepa_city: string;
  sepa_iban_number: string;
  sepa_bic_number: string;
  sepa_identifier: string;
  sepa_template_id: string;
  default_invoice_layout: string;
  invoice_sync_service: string;
  exact_integration_link: string;
  winbooks_user: string;
  winbooks_exchange_token: string;
  winbooks_folder: string;
  invoice_sync_disable_document_number_sync: boolean;
  odoo_host: string;
  odoo_database: string;
  odoo_username: string;
  odoo_password: string;
  invoice_sync_default_account_number: string;
  analytics_usage: string;
  analytic_sync_destination: string;
  send_unpaid_invoice_reminder_email: boolean;
  unpaid_invoice_reminder_delay: string;
  unpaid_invoice_reminder_title: string;
  unpaid_invoice_reminder_email_template_id: number;
  unpaid_invoice_reminder_delay2: string;
  unpaid_invoice_reminder_title2: string;
  unpaid_invoice_reminder_email2_template_id: number;
  unpaid_invoice_reminder_delay3: string;
  unpaid_invoice_reminder_title3: string;
  unpaid_invoice_reminder_email3_template_id: number;
}
export interface ContractsSetting {
  id: number;
  send_contract_proposed_email: boolean;
  contract_proposed_email_title: string;
  contract_proposed_email_template_id: number;
  email_blind_copy_contract_proposed: string;
  send_contract_requested_email: boolean;
  contract_requested_email_profile_id: number;
  email_blind_copy_contract_requested: string;
}
export interface PointOfSalesSetting {
  id: number;
  pos_discounts_allowed: boolean;
  pos_refunds_allowed: boolean;
  automatic_pos_session_errors: boolean;
  nepting_merchant_id: number;
  send_invoice_from_pos: boolean;
  invoice_from_pos_title: string;
  invoice_from_pos_template_id: number;
}
export interface OnlineShopSetting {
  id: number;
  activate_shopping_cart: boolean;
  money_transfer_activated: boolean;
  money_transfer_confirmation_page_info: string;
  stripe_activated: boolean;
  bancontact_activated: boolean;
  payfip_activated: boolean;
  stripe_public_key: string;
  stripe_private_key: string;
  stripe_webhook_key: string;
  stripe_client_id: string;
  stripe_connect_link: string;
  payfip_numcli: string;
  payfip_status: string;
  allow_coupon: string;
  send_payment_request_email: boolean;
  payment_request_email_title: string;
  payment_request_email_template_id: number;
  send_receipt_email: boolean;
  receipt_email_title: string;
  receipt_email_template_id: number;
}
export interface WebsiteSetting {
  id: number;
  allow_access_public_website: boolean;
  public_footer: string;
  seo_title: string;
  seo_description: string;
  google_site_verification_code: string;
  option_group_id: number;
}
export interface AccessControlSetting {
  id: number;
}

export interface HiddenSetting {
  id: number;
  multisports: string;
  team_sports: string;
  activity_page_header: string;
  register_t_and_c_post_id: string;
  enrollment_t_and_c_post_id: string;
  terms_of_sale_post_id: string;
  booking_t_and_c_post_id: string;
  invoice_center_data: string;
  public_css: string;
  stylesheet: string;
  interior_regulations_post_id: string;
  privacy_policy_post_id: string;
  invoice_from_ticket_contact_id: string;
  accounting_journal_for_ticket_id: string;
  ticket_header: string;
  ticket_footer: string;
  accounting_integration_config: string;
  admin_css: string;
  group_subvention_on_invoices: string;
  subvention_mode: string;
  default_subsidizing_contact_id: string;
  hide_true_cost_subventions_on_invoices: string;
  custom_contact_fields: string;
  allow_access_new_contact_form: string;
  show_new_contact_relation1_form: string;
  show_new_contact_relation2_form: string;
  new_contact_form_title: string;
  new_contact_form_description: string;
  show_new_contact_birthdate: string;
  show_new_contact_gender: string;
  show_new_contact_address_fields: string;
  show_new_contact_phone1: string;
  show_new_contact_phone2: string;
  show_new_contact_email1: string;
  show_new_contact_email2: string;
  show_new_contact_id_card_number: string;
  show_new_contact_passport_number: string;
  show_new_contact_registration_number: string;
  show_new_contact_external_reference: string;
  age_limit: string;
  show_new_contact_relation1_birthdate: string;
  show_new_contact_relation1_gender: string;
  show_new_contact_relation1_address_fields: string;
  show_new_contact_relation1_phone1: string;
  show_new_contact_relation1_phone2: string;
  show_new_contact_relation1_email1: string;
  show_new_contact_relation1_email2: string;
  show_new_contact_relation1_id_card_number: string;
  show_new_contact_relation1_passport_number: string;
  show_new_contact_relation1_registration_number: string;
  show_new_contact_relation1_external_reference: string;
  show_new_contact_relation2_birthdate: string;
  show_new_contact_relation2_gender: string;
  show_new_contact_relation2_address_fields: string;
  show_new_contact_relation2_phone1: string;
  show_new_contact_relation2_phone2: string;
  show_new_contact_relation2_email1: string;
  show_new_contact_relation2_email2: string;
  show_new_contact_relation2_id_card_number: string;
  show_new_contact_relation2_passport_number: string;
  show_new_contact_relation2_registration_number: string;
  show_new_contact_relation2_external_reference: string;
  send_summons_email: string;
  summons_email_title: string;
  summons_email_template_id: string;
  email_blind_copy_summons: string;
  admin_send_summons_email: string;
  use_new_cms: string;
  contact_tags_for_new_contacts: string;
  send_new_contact_email: string;
  new_contact_email_title: string;
  new_contact_email_template_id: string;
  email_blind_copy_new_contact: string;
  anybuddy_contact_id: string;
  anybuddy_with_price: string;
  tppwb_sync_tennis_activated: string;
  club_id_tppwb_sync_tennis: string;
  login_tppwb_sync_tennis: string;
  // password_tppwb_sync_tennis: string;
  tppwb_sync_padel_activated: string;
  club_id_tppwb_sync_padel: string;
  login_tppwb_sync_padel: string;
  use_structured_communication: string;
  free_bookings_activated: string;
  use_pos_v2: string;
  use_article_impacts: string;
  enable_contact_card: string;
  contact_card_layout: string;
  use_multiplayer_bookings: string;
  use_contact_relations_v2: string;
}
export class CenterSettingByModule {
  id: number;
  center_id: number;
  globalSetting: GlobalSetting;
  usersSetting: UsersSetting;
  contactsSetting: ContactsSetting;
  subscriptionsSetting: SubscriptionsSetting;
  bookingsSetting: BookingsSetting;
  activitiesSetting: ActivitiesSetting;
  accountingSetting: AccountingSetting;
  contractsSetting: ContractsSetting;
  pointOfSalesSetting: PointOfSalesSetting;
  onlineShopSetting: OnlineShopSetting;
  websiteSetting: WebsiteSetting;
  accessControlSetting: AccessControlSetting;
  hiddenSetting: HiddenSetting;
}

export class CenterSetting {
  id: number;
  center_id: number;
  default_slot_duration: number;
  default_start_hour: string;
  default_end_hour: string;
  default_language: string;
  logo_url: string;
  header_image_url: string;
  name: string;
  street: string;
  street2: string;
  zip: string;
  city: string;
  country: string;
  vat_number: string;
  phone1: string;
  email1: string;
  website: string;
  center_type: string;
  multisports: boolean;
  team_sports: boolean;
  register_t_and_c_post_id: number;
  enrollment_t_and_c_post_id: number;
  interior_regulations_post_id: number;
  privacy_policy_post_id: number;
  terms_of_sale: number;
  money_transfer_activated: boolean;
  money_transfer_confirmation_page_info: string;
  bancontact_activated: boolean;
  allow_coupon: boolean;
  activate_shopping_cart: boolean;
  stripe_activated: boolean;
  stripe_public_key: string;
  stripe_private_key: string;
  stripe_webhook_key: string;
  payfip_activated: boolean;
  available_languages: string[];
  show_menu_entries: string[];
  show_menu_entries_for_organization: string[];
  show_birthdate: string;
  show_gender: string;
  show_address_fields: string;
  show_phone: string;
  show_phone2: string;
  show_email2: string;
  show_id_card_number: string;
  show_passport_number: string;
  show_registration_number: string;
  show_external_reference: string;
  mandatory_birthdate: string;
  mandatory_gender: string;
  mandatory_address_fields: string;
  mandatory_phone: string;
  mandatory_id_card_number: string;
  created_at: string;
  activity_page_header: string;
  home_page: string;
  contact_profile_home_page: string;
  deleted_at: string;
  updated_at: string;
  background: object;
  show_activity_categories_in_profile: boolean;
  ticket_header: string;
  ticket_footer: string;
  accounting_integration_settings?: AccountingIntegrationSettings;
  advanced_prefix_management?: AdvancedPrefixManagement;
  analytics_usage: boolean;
  activate_sepa_direct_debit: boolean;
  group_subvention_on_invoices: boolean;
  show_activity_management_page?: boolean;
  subvention_mode: string;
  default_invoice_layout: string;
  cancelled_unpaid_booking_remain_due: boolean;
  cancelled_paid_booking_generate_refund: boolean;
  allow_booking_refunds: boolean;
  send_summons_email: boolean;
  summons_email_title: string;
  summons_email_template_id: number;
  email_blind_copy_summons: string[];
  admin_send_summons_email: boolean;
  use_new_cms: boolean;
  free_bookings_activated: boolean;
  use_pos_v2: boolean;
  use_contact_relations_v2: boolean;
  enable_contact_card: boolean;
  use_article_impacts: boolean;
  use_multiplayer_bookings: boolean;
  display_booking_calendar_with_contact_color: boolean;
  nepting_merchant_id: string;
  source_color_to_display_booking_calendar: string;
  send_subscription_recredit_email: boolean;
  admin_send_subscription_recredit_email: boolean;
  subscription_recredit_email_title: string;
  subscription_recredit_email_template_id: number;
  email_blind_copy_subscription_recredit: string;
  pos_discounts_allowed: boolean;
  pos_refunds_allowed: boolean;
  invoice_from_ticket_contact_id: number;
  access_control_config?: AccessControlConfigInterface;
  subscription_payment_request_email_template_id: number;
  subscription_payment_reminder_email_template_id: number;
  enrollment_payment_request_email_template_id: number;
  enrollment_payment_reminder_email_template_id: number;
  tppwb_sync_tennis_activated: boolean;
  tppwb_sync_padel_activated: boolean;
}
